import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBilling } from "../lib/billing";
import { ForkKnife, House, Plus, Tag, SignOut, CrownSimple, ChartLineUp, Scales, UserCircle } from "@phosphor-icons/react";
import SupportChat from "../components/SupportChat";

export default function AppShell() {
  const { user, logout } = useAuth();
  const { status } = useBilling();
  const navigate = useNavigate();

  const link = ({ isActive }) =>
    `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
      isActive
        ? "bg-[hsl(var(--foreground))] text-white"
        : "text-[hsl(var(--foreground))] hover:bg-white/70"
    }`;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[hsl(var(--background))]/80 border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2" data-testid="app-brand-logo">
            <ForkKnife size={24} weight="duotone" className="text-[hsl(var(--primary))]" />
            <span className="font-serif text-xl">MIZETA</span>
            {status?.is_pro && (
              <span data-testid="pro-badge" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-[10px] font-semibold uppercase tracking-wider">
                <CrownSimple size={10} weight="fill" /> {status.active_plan === "admin" ? "Admin" : "Pro"}
              </span>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/app" end className={link} data-testid="nav-library"><House size={16} /> Recetario</NavLink>
            <NavLink to="/app/categories" className={link} data-testid="nav-categories"><Tag size={16} /> Categorías</NavLink>
            <NavLink to="/app/equivalencias" className={link} data-testid="nav-equivalences"><Scales size={16} /> Medidas</NavLink>
            {!status?.is_pro && (
              <NavLink to="/app/pro" className={link} data-testid="nav-pro"><CrownSimple size={16} /> Pro</NavLink>
            )}
            {user?.is_admin && (
              <NavLink to="/app/admin" className={link} data-testid="nav-admin"><ChartLineUp size={16} /> Admin</NavLink>
            )}
            <NavLink to="/app/cuenta" className={link} data-testid="nav-account"><UserCircle size={16} /> Cuenta</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/app/extract")}
              data-testid="quick-extract-btn"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold btn-press hover-lift"
            >
              <Plus size={16} weight="bold" /> Nueva receta
            </button>
            {user && (
              <div className="flex items-center gap-2">
                {user.picture && <Link to="/app/cuenta" data-testid="account-avatar-link"><img src={user.picture} alt="Abrir cuenta" className="w-8 h-8 rounded-full border border-[hsl(var(--border))]" /></Link>}
                <button onClick={logout} data-testid="logout-btn" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" title="Cerrar sesión">
                  <SignOut size={20} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* mobile bottom-friendly secondary nav */}
        <div className="md:hidden border-t border-[hsl(var(--border))] px-4 py-2 flex gap-2 overflow-x-auto">
          <NavLink to="/app" end className={link} data-testid="mobile-nav-library"><House size={16} /> Recetario</NavLink>
          <NavLink to="/app/categories" className={link} data-testid="mobile-nav-categories"><Tag size={16} /> Categorías</NavLink>
          <NavLink to="/app/equivalencias" className={link} data-testid="mobile-nav-equivalences"><Scales size={16} /> Medidas</NavLink>
          {!status?.is_pro && (
            <NavLink to="/app/pro" className={link} data-testid="mobile-nav-pro"><CrownSimple size={16} /> Pro</NavLink>
          )}
          <NavLink to="/app/cuenta" className={link} data-testid="mobile-nav-account"><UserCircle size={16} /> Cuenta</NavLink>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Outlet />
      </main>

      <SupportChat />
    </div>
  );
}
